import { signOut as betterAuthSignOut, useSession } from '@jbroll/rowboat-auth-betterauth-react';
import { useEffect, useLayoutEffect, useState } from 'react';
import { clearSyncToken, setSyncTokenAuthor } from '@/lib/syncToken';

// The last signed-in author, so a start with no network opens that account's store.
export const LAST_AUTHOR_KEY = 'rosa-house-last-author';

export function rememberedAuthor(): string | null {
  try {
    return localStorage.getItem(LAST_AUTHOR_KEY);
  } catch {
    return null;
  }
}

function rememberAuthor(author: string | null): void {
  try {
    if (author) localStorage.setItem(LAST_AUTHOR_KEY, author);
    else localStorage.removeItem(LAST_AUTHOR_KEY);
  } catch {
    // Storage blocked: a start with no network runs anonymous, as before.
  }
}

export interface SessionState {
  data: { user: { id: string } } | null;
  error?: unknown;
  isPending: boolean;
}

/**
 * True when the session request got no answer from the auth server. better-auth keeps `data`
 * at null after a failed fetch, which would otherwise read as signed out.
 */
function sessionUnreachable(error: unknown): boolean {
  if (!error) return false;
  const status = (error as { status?: unknown }).status;
  return typeof status !== 'number' || status === 0 || status >= 500;
}

/** The author the app acts as: the live session, else the remembered one until the server answers. */
export function resolveAuthor(session: SessionState, remembered: string | null): string | null {
  if (session.data) return session.data.user.id;
  if (session.isPending || sessionUnreachable(session.error)) return remembered;
  return null;
}

export function useAuthor(): string | null {
  const session = useSession() as SessionState;
  const author = resolveAuthor(session, rememberedAuthor());
  const answered = !!session.data || (!session.isPending && !sessionUnreachable(session.error));
  useEffect(() => {
    if (answered) rememberAuthor(author);
  }, [answered, author]);
  return author;
}

// An object, so tests can replace the reload this module calls.
export const page = { reload: () => window.location.reload() };

/**
 * Reloads the page when the author changes from a signed-in account to anything else, so no
 * token, queue runner or graph of the old account survives; true from then on. Signing in from
 * anonymous keeps the page, since the claim carries the anonymous store over.
 */
export function useReloadOnAccountChange(author: string | null): boolean {
  const [actedAs, setActedAs] = useState(author);
  if (author !== null && actedAs === null) setActedAs(author);
  const switched = actedAs !== null && author !== actedAs;
  // A layout effect, so the token is bound before any child's effect starts a sync.
  useLayoutEffect(() => {
    if (!switched) {
      setSyncTokenAuthor(author);
      return;
    }
    rememberAuthor(author);
    clearSyncToken();
    page.reload();
  }, [author, switched]);
  return switched;
}

/**
 * Signs out and reloads, so nothing held in memory for the old account (sync token, upload and
 * identify runners) outlives it. A failed sign-out throws and leaves the session and the page as they were.
 */
export async function signOut(): Promise<void> {
  try {
    const result = (await betterAuthSignOut()) as { error?: unknown } | undefined;
    if (result?.error) throw result.error;
  } catch (err) {
    if (!navigator.onLine) throw new Error('Sign-out needs a connection.');
    throw err instanceof Error ? err : new Error(refusalMessage(err));
  }
  rememberAuthor(null);
  clearSyncToken();
  page.reload();
}

function refusalMessage(refusal: unknown): string {
  const { message, status } = refusal as { message?: unknown; status?: unknown };
  if (typeof message === 'string' && message) return message;
  return `Sign-out was refused (${String(status)})`;
}
